import React from "react";

import cloneDeep from "lodash/cloneDeep";

import Alert from "@cloudscape-design/components/alert";
import Modal from "@cloudscape-design/components/modal";
import Input from "@cloudscape-design/components/input";
import Button from "@cloudscape-design/components/button";
import Box from "@cloudscape-design/components/box";
import FormField from "@cloudscape-design/components/form-field";
import { SpaceBetween } from "@cloudscape-design/components";
import Select from "@cloudscape-design/components/select";

/* Recoil */
import { useRecoilValue, useSetRecoilState } from "recoil";
import { configStore, reasonsWithdrawalsForDenial } from "../stores/config";
import { gameStore } from "../stores/games";
import {
	createBalanceCreditLimitRequestToken,
	createBalanceCreditLimitState,
	getBalanceCreditLimitContext,
	isSameBalanceCreditLimitContext,
	normalizeBalanceCreditLimitState,
	playerModalStore,
} from "../stores/playerModal";
import { playerPageStore } from "../stores/playerPage";
import { playerStore } from "../stores/players";
import { withdrawalStore } from "../stores/withdrawals";
import { userStore } from "../stores/access";
import { getRecoil } from "recoil-nexus";
/* ------- */

const MAX_VISIBLE_GAMES = 100;
const VIP_FREESPINS_MODAL_TYPE = 11;

const isSamePlayerContext = (playerPage, uid, project) => {
	if (!playerPage?.data || typeof playerPage.data.project === "undefined" || playerPage.data.project === null || typeof project === "undefined" || project === null) return false;

	return playerPage.uid === uid && String(playerPage.data.project) === String(project);
};

const getReloadableLogModeSignature = (log) => {
	if (!log || !(Number(log.type) < 5)) return null;

	return {
		type: log.type,
		min: log.min,
		max: log.max,
		limit: log.limit,
		page: log.page,
	};
};

const isSameLogModeSignature = (log, signature) =>
	Boolean(log && signature) &&
	["type", "min", "max", "limit", "page"].every((field) => Object.is(log[field], signature[field]));

const formatGameLabel = (gameName, gameId) => `${gameName || "Game"} #${gameId}`;

const buildActiveGameOptions = (games = []) =>
	games
		.filter((game) => Number(game?.active) === 1 && game?.gameID)
		.slice()
		.sort((left, right) => {
			const leftName = String(left?.name || "").toLowerCase();
			const rightName = String(right?.name || "").toLowerCase();

			return leftName.localeCompare(rightName, undefined, { numeric: true, sensitivity: "base" }) || Number(left.gameID) - Number(right.gameID);
		})
		.map((game) => ({
			value: game.gameID.toString(),
			label: formatGameLabel(game.name, game.gameID),
		}));

class PlayerModal extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			modalWithdrawal: false,
			promoReasonInput: "",
			showPromoReasonInput: false,
			promoGames: [],
			filteredPromoGames: [],
			balanceMode: "debit",
			balanceCreditAmount: "",
			balanceCreditSubmitting: false,
			writeOffPresets: [],
			writeOffPresetsLoading: false,
			writeOffPresetsLoaded: false,
			writeOffPresetsError: "",
			writeOffPresetSubmitting: false,
			writeOffPresetError: "",
		};
		this.writeOffPresetsRequestId = 0;
		this.writeOffPresetApplyRequestId = 0;
	}

	componentDidMount() {
		this.syncPromoGames();

		if (this.props.playerModal.show && this.props.playerModal.type === 0) {
			this.initializeBalanceModal();
		}

		if (this.props.playerModal.type === 4 || this.props.playerModal.type === 5) {
			this.setState({
				modalWithdrawal: true,
			});
		}

		if (this.props.playerModal.type === 6) {
			this.loadPromoReasonOptions();
		}

		if (this.props.playerModal.type === VIP_FREESPINS_MODAL_TYPE) {
			this.loadFreespinsRuleOptions();
		}
	}

	componentDidUpdate(prevProps) {
		if (prevProps.game.data !== this.props.game.data) {
			this.syncPromoGames();
		}

		const balanceModalOpened = (!prevProps.playerModal.show || prevProps.playerModal.type !== 0) && this.props.playerModal.show && this.props.playerModal.type === 0;
		const balanceModalContextChanged =
			this.props.playerModal.show &&
			this.props.playerModal.type === 0 &&
			(prevProps.playerPage.uid !== this.props.playerPage.uid ||
				!isSameBalanceCreditLimitContext(
					getBalanceCreditLimitContext(prevProps.playerPage, prevProps.user),
					this.getBalanceCreditContext(),
				));

		if (balanceModalOpened || balanceModalContextChanged) {
			this.initializeBalanceModal();
		}

		if (prevProps.playerModal.type !== VIP_FREESPINS_MODAL_TYPE && this.props.playerModal.type === VIP_FREESPINS_MODAL_TYPE) {
			this.loadFreespinsRuleOptions();
		}
	}

	componentWillUnmount() {
		this.writeOffPresetsRequestId += 1;
		this.writeOffPresetApplyRequestId += 1;
	}

	hasBalanceDebitAccess() {
		return this.props.user.level >= 6 || this.props.user.showColumn.includes("balanceDownGrade");
	}

	hasBalanceCreditAccess() {
		return this.props.user.level >= 6 || this.props.user.showColumn.includes("balanceCredit");
	}

	getBalanceCreditLimit() {
		return this.props.playerModal.balanceCreditLimit;
	}

	getBalanceCreditContext() {
		return getBalanceCreditLimitContext(this.props.playerPage, this.props.user);
	}

	isBalanceCreditAvailable() {
		const limit = this.getBalanceCreditLimit();

		return (
			limit.loaded &&
			!limit.error &&
			isSameBalanceCreditLimitContext(limit, this.getBalanceCreditContext()) &&
			limit.enabled === true &&
			limit.remainingMinor > 0
		);
	}

	isBalanceCreditMode() {
		return this.props.playerModal.type === 0 && this.hasBalanceCreditAccess() && (this.state.balanceMode === "credit" || !this.hasBalanceDebitAccess());
	}

	getDefaultBalanceMode() {
		if (this.hasBalanceDebitAccess()) return "debit";
		if (this.hasBalanceCreditAccess()) return "credit";

		return "debit";
	}

	initializeBalanceModal() {
		const balanceMode = this.getDefaultBalanceMode();
		this.writeOffPresetsRequestId += 1;
		this.writeOffPresetApplyRequestId += 1;

		this.setState(
			{
				balanceMode,
				balanceCreditAmount: "",
				balanceCreditSubmitting: false,
				writeOffPresets: [],
				writeOffPresetsLoading: false,
				writeOffPresetsLoaded: false,
				writeOffPresetsError: "",
				writeOffPresetSubmitting: false,
				writeOffPresetError: "",
			},
			() => {
				if (balanceMode === "credit" && this.hasBalanceCreditAccess()) {
					this.loadBalanceCreditLimit();
				} else if (balanceMode === "debit" && this.hasBalanceDebitAccess()) {
					this.loadWriteOffPresets();
				}
			},
		);
	}

	normalizeWriteOffPresets(presets) {
		if (!Array.isArray(presets)) throw new Error("Invalid write-off presets response");

		return presets.map((preset) => {
			if (
				!preset ||
				typeof preset !== "object" ||
				Array.isArray(preset) ||
				typeof preset.label !== "string" ||
				!preset.label.trim() ||
				!Number.isSafeInteger(preset.targetMinor) ||
				preset.targetMinor <= 0 ||
				preset.targetMinor % 100 !== 0
			) {
				throw new Error("Invalid write-off presets response");
			}

			return {
				label: preset.label,
				targetMinor: preset.targetMinor,
			};
		});
	}

	loadWriteOffPresets() {
		if (!this.hasBalanceDebitAccess()) return;

		this.writeOffPresetsRequestId += 1;
		const requestId = this.writeOffPresetsRequestId;
		const login = this.props.user.login;
		const brand = this.props.user.brand?.id;
		this.setState({
			writeOffPresets: [],
			writeOffPresetsLoading: true,
			writeOffPresetsLoaded: false,
			writeOffPresetsError: "",
			writeOffPresetError: "",
		});

		this.props.Interface.request("get", { class: "settings", module: "staff", method: "writeOffPresets" })
			.then((presets) => {
				const normalizedPresets = this.normalizeWriteOffPresets(presets);
				if (
					requestId !== this.writeOffPresetsRequestId ||
					this.state.balanceMode !== "debit" ||
					!this.props.playerModal.show ||
					this.props.playerModal.type !== 0 ||
					this.props.user.login !== login ||
					String(this.props.user.brand?.id) !== String(brand)
				) return;

				this.setState({
					writeOffPresets: normalizedPresets,
					writeOffPresetsLoading: false,
					writeOffPresetsLoaded: true,
					writeOffPresetsError: "",
				});
			})
			.catch((error) => {
				if (
					requestId !== this.writeOffPresetsRequestId ||
					this.state.balanceMode !== "debit" ||
					!this.props.playerModal.show ||
					this.props.playerModal.type !== 0 ||
					this.props.user.login !== login ||
					String(this.props.user.brand?.id) !== String(brand)
				) return;

				const errorText = this.getErrorText(error, "Failed to load balance target presets");
				this.setState({
					writeOffPresets: [],
					writeOffPresetsLoading: false,
					writeOffPresetsLoaded: false,
					writeOffPresetsError: errorText,
				});
				if (!error?.timestamp) this.props.Interface.addNoty("Error", errorText);
			});
	}

	openBalanceDebit() {
		if (this.state.balanceCreditSubmitting || this.state.writeOffPresetSubmitting || !this.hasBalanceDebitAccess()) return;

		this.setState({ balanceMode: "debit", writeOffPresetError: "" }, () => {
			if (!this.state.writeOffPresetsLoaded && !this.state.writeOffPresetsLoading) {
				this.loadWriteOffPresets();
			}
		});
	}

	formatWriteOffPresetTarget(targetMinor) {
		return `${String(targetMinor / 100).replace(/\B(?=(\d{3})+(?!\d))/g, "\u00a0")} RUB`;
	}

	getWriteOffReasonOptions() {
		const standardReasons = this.props.playerModal.currentOption.reason;
		if (!this.state.writeOffPresetsLoaded || this.state.writeOffPresetsError || !this.state.writeOffPresets.length) {
			return standardReasons;
		}

		return [
			...standardReasons,
			{
				label: "Balance target presets",
				options: this.state.writeOffPresets.map((preset, index) => ({
					label: `${preset.label} — target balance: ${this.formatWriteOffPresetTarget(preset.targetMinor)}`,
					value: `write-off-preset:${index}:${preset.label}`,
					isWriteOffPreset: true,
					presetLabel: preset.label,
					targetMinor: preset.targetMinor,
				})),
			},
		];
	}

	getSelectedWriteOffPreset() {
		const selectedOption = this.props.playerModal.value2;
		return selectedOption && selectedOption.isWriteOffPreset === true ? selectedOption : null;
	}

	selectWriteOffReason(selectedOption) {
		const isWriteOffPreset = selectedOption?.isWriteOffPreset === true;
		this.setState({ writeOffPresetError: "" });
		this.props.setPlayerModal({
			...this.props.playerModal,
			value2: selectedOption,
			...(isWriteOffPreset ? { value: "" } : {}),
		});
	}

	formatBalanceCreditMinor(amountMinor) {
		const amount = this.formatBalanceCreditCommentAmount(amountMinor);
		const amountParts = amount.split(".");
		return `${amountParts[0].replace(/\B(?=(\d{3})+(?!\d))/g, "\u00a0")},${amountParts[1]}`;
	}

	formatBalanceCreditCommentAmount(amountMinor) {
		const digits = amountMinor.toString().padStart(3, "0");
		return `${digits.slice(0, -2)}.${digits.slice(-2)}`;
	}

	getBalanceCreditStatus() {
		const limit = this.getBalanceCreditLimit();

		if (limit.loading) return "loading";
		if (limit.error) return "unavailable";
		if (!limit.loaded || !isSameBalanceCreditLimitContext(limit, this.getBalanceCreditContext())) return "loading";
		if (!limit.enabled) return "not-assigned";
		if (limit.remainingMinor === 0) return "exhausted";
		if (this.isBalanceCreditAvailable()) return "available";

		return "unavailable";
	}

	renderBalanceCreditMetrics(remainingColor) {
		const limit = this.getBalanceCreditLimit();

		return (
			<Box margin={{ top: "xs" }}>
				<SpaceBetween direction="horizontal" size="l">
					<Box variant="span">
						<Box variant="span" color="text-body-secondary">Operation limit:</Box> {this.formatBalanceCreditMinor(limit.operationLimitMinor)} RUB
					</Box>
					<Box variant="span">
						<Box variant="span" color="text-body-secondary">Remaining:</Box>{" "}
						<Box variant="span" color={remainingColor}>
							{this.formatBalanceCreditMinor(limit.remainingMinor)} RUB
						</Box>
					</Box>
				</SpaceBetween>
			</Box>
		);
	}

	renderBalanceCreditStatus() {
		const status = this.getBalanceCreditStatus();
		const limit = this.getBalanceCreditLimit();

		switch (status) {
			case "loading":
				return <Box margin={{ top: "xs" }}>Credit limit: Loading...</Box>;
			case "not-assigned":
				return (
					<Box margin={{ top: "xs" }}>
						<Box><b>Credit limit: Not assigned</b></Box>
						<Box color="text-body-secondary">Credit is unavailable until a limit is assigned.</Box>
					</Box>
				);
			case "exhausted":
				return this.renderBalanceCreditMetrics("text-status-error");
			case "unavailable":
				return (
					<Box margin={{ top: "xs" }}>
						<Box color="text-status-error"><b>Credit limit: Unavailable</b></Box>
						{limit.error ? <Box color="text-status-error">{limit.error}</Box> : <></>}
					</Box>
				);
			case "available":
				return this.renderBalanceCreditMetrics("text-status-success");
			default:
				return <></>;
		}
	}

	parseBalanceCreditAmountMinor(amount) {
		if (typeof amount !== "string" || !/^[0-9]+(?:\.[0-9]{1,2})?$/.test(amount)) {
			return null;
		}

		const amountParts = amount.split(".");
		const fractionPart = (amountParts[1] || "").padEnd(2, "0");
		const amountMinor = Number(amountParts[0] + fractionPart);

		return Number.isSafeInteger(amountMinor) && amountMinor > 0 ? amountMinor : null;
	}

	getBalanceCreditAmountError() {
		const amount = this.state.balanceCreditAmount;

		if (!amount) return "Amount is required";

		const amountMinor = this.parseBalanceCreditAmountMinor(amount);
		if (amountMinor === null) return "Enter a positive RUB amount with no more than two decimal digits";

		const limit = this.getBalanceCreditLimit();
		if (this.isBalanceCreditAvailable() && amountMinor > limit.operationLimitMinor && limit.operationLimitMinor <= limit.remainingMinor) {
			return "Amount exceeds the operation limit";
		}
		if (this.isBalanceCreditAvailable() && amountMinor > limit.remainingMinor) {
			return "Amount exceeds the remaining limit";
		}

		return "";
	}

	isBalanceCreditSubmitDisabled() {
		const limit = this.getBalanceCreditLimit();

		return (
			!this.hasBalanceCreditAccess() ||
			limit.loading ||
			!this.isBalanceCreditAvailable() ||
			Boolean(this.getBalanceCreditAmountError()) ||
			this.state.balanceCreditSubmitting
		);
	}

	openBalanceCredit() {
		if (this.state.balanceCreditSubmitting || this.state.writeOffPresetSubmitting || !this.hasBalanceCreditAccess()) return;

		if (this.state.writeOffPresetsLoading) this.writeOffPresetsRequestId += 1;
		if (this.getSelectedWriteOffPreset()) {
			this.props.setPlayerModal({ ...this.props.playerModal, value2: "" });
		}
		this.setState({ balanceMode: "credit", writeOffPresetsLoading: false }, () => {
			this.loadBalanceCreditLimit();
		});
	}

	loadBalanceCreditLimit() {
		if (!this.hasBalanceCreditAccess()) return;

		const context = this.getBalanceCreditContext();
		if (context.project === null || context.brand === null || !context.login) return;

		const requestToken = createBalanceCreditLimitRequestToken();
		this.props.setPlayerModal((playerModal) => ({
			...playerModal,
			balanceCreditLimit: {
				...createBalanceCreditLimitState({ ...context, requestToken }),
				loading: true,
			},
		}));

		this.props.Interface.request("get", { class: "users", module: "wallet", method: "balanceCreditLimit" })
			.then((result) => {
				const limitState = normalizeBalanceCreditLimitState(result);
				if (!isSameBalanceCreditLimitContext(this.getBalanceCreditContext(), context)) return;

				this.props.setPlayerModal((playerModal) => {
					if (playerModal.balanceCreditLimit?.requestToken !== requestToken) return playerModal;

					return {
						...playerModal,
						balanceCreditLimit: {
							...limitState,
							...context,
							loading: false,
							loaded: true,
							error: null,
							requestToken,
						},
					};
				});

			})
			.catch((error) => {
				if (!isSameBalanceCreditLimitContext(this.getBalanceCreditContext(), context)) return;

				const errorText = this.getErrorText(error, "Failed to load balance credit limit");
				this.props.setPlayerModal((playerModal) => {
					if (playerModal.balanceCreditLimit?.requestToken !== requestToken) return playerModal;

					return {
						...playerModal,
						balanceCreditLimit: {
							...createBalanceCreditLimitState({ ...context, requestToken }),
							error: errorText,
						},
					};
				});

				if (!error?.timestamp) {
					this.props.Interface.addNoty("Error", errorText);
				}
			});
	}

	toggleValueModal(type) {
		this.writeOffPresetsRequestId += 1;
		this.writeOffPresetApplyRequestId += 1;
		this.setState({
			promoReasonInput: "",
			showPromoReasonInput: false,
			balanceMode: this.getDefaultBalanceMode(),
			balanceCreditAmount: "",
			balanceCreditSubmitting: false,
			writeOffPresets: [],
			writeOffPresetsLoading: false,
			writeOffPresetsLoaded: false,
			writeOffPresetsError: "",
			writeOffPresetSubmitting: false,
			writeOffPresetError: "",
		});

		if (typeof type !== "undefined") this.props.setPlayerModal((playerModal) => ({ ...playerModal, show: 0, type, value: "", value2: "", promoReasonSelected: null, promoGameSelected: null, vipWager: "", vipCap: "" }));
		else this.props.setPlayerModal((playerModal) => ({ ...playerModal, item: false, show: 0, value: "", value2: "", promoReasonSelected: null, promoGameSelected: null, vipWager: "", vipCap: "" }));
	}

	getPromoReasonOptions(reasons = []) {
		return [...new Set(reasons)].map((button) => ({ label: button, value: button }));
	}

	syncPromoGames() {
		const promoGames = buildActiveGameOptions(this.props.game?.data || []);

		this.setState({
			promoGames,
			filteredPromoGames: promoGames.slice(0, MAX_VISIBLE_GAMES),
		});
	}

	handlePromoGameSearch(filteringText) {
		const currentFilter = String(filteringText || "")
			.trim()
			.toLowerCase();
		const filteredPromoGames = currentFilter.length === 0 ? this.state.promoGames : this.state.promoGames.filter((game) => game.label.toLowerCase().includes(currentFilter) || game.value.includes(currentFilter));

		this.setState({
			filteredPromoGames: filteredPromoGames.slice(0, MAX_VISIBLE_GAMES),
		});
	}

	loadPromoReasonOptions() {
		this.props.setPlayerModal((prevState) => ({
			...prevState,
			promoReasonsLoading: true,
			promoReasonsError: "",
		}));

		this.props.Interface.request("get", { class: "users", module: "promo", method: "getGivePromoReasons" })
			.then((reasons) => {
				this.props.setPlayerModal((prevState) => ({
					...prevState,
					promoReasonOptions: this.getPromoReasonOptions(Array.isArray(reasons) ? reasons : []),
					promoReasonsLoading: false,
					promoReasonsError: "",
				}));
			})
			.catch((error) => {
				const errorText = error?.text || error?.message || error?.toString?.() || "Failed to load reasons";

				this.props.setPlayerModal((prevState) => ({
					...prevState,
					promoReasonOptions: [],
					promoReasonsLoading: false,
					promoReasonsError: errorText,
				}));
				this.props.Interface.addNoty("Error", errorText);
			});
	}

	buildPromoNote() {
		if (!this.props.playerModal.promoReasonSelected) return "";

		if (this.props.playerModal.promoReasonSelected.value === "VIP") {
			const vipWager = String(this.props.playerModal.vipWager || "").trim();
			const vipCap = String(this.props.playerModal.vipCap || "").trim();

			if (!vipWager || !vipCap) return "";

			return `VIP_Wager${vipWager}Cap${vipCap}`;
		}

		if (this.props.playerModal.promoReasonSelected.value === "bag_slot") {
			if (!this.props.playerModal.promoGameSelected) return "";

			return this.props.playerModal.promoReasonSelected.value + ":" + this.props.playerModal.promoGameSelected.value;
		}

		return this.props.playerModal.promoReasonSelected.value;
	}

	isPromoValid() {
		if (!this.props.playerModal.value) return false;
		if (!this.props.playerModal.promoReasonSelected) return false;
		if (
			this.props.playerModal.promoReasonSelected.value === "VIP" &&
			(!String(this.props.playerModal.vipWager || "").trim().length || !String(this.props.playerModal.vipCap || "").trim().length)
		) return false;
		if (this.props.playerModal.promoReasonSelected.value === "bag_slot" && !this.props.playerModal.promoGameSelected) return false;

		return true;
	}

	getErrorText(error, fallback = "Unknown error") {
		if (typeof error === "string") return error;
		if (error?.message) return error.message;
		if (error?.text) return error.text;
		if (error && typeof error === "object") return JSON.stringify(error);

		return fallback;
	}

	getPlayerCurrency() {
		return this.props.playerPage.data?.currency || "RUB";
	}

	getPlayerProject() {
		return typeof this.props.playerPage.data?.project !== "undefined" ? this.props.playerPage.data.project : 0;
	}

	isVipFreespinsModal() {
		return this.props.playerModal.type === VIP_FREESPINS_MODAL_TYPE;
	}

	resetVipFreespinsCheckFields() {
		return {
			vipCheckStatus: "idle",
			vipCheckLogId: null,
			vipCheckError: "",
			vipCheckResponse: null,
		};
	}

	updateFreespinsFields(updates, resetCheck = false) {
		this.props.setPlayerModal((prevState) => ({
			...prevState,
			...updates,
			...(resetCheck ? this.resetVipFreespinsCheckFields() : {}),
		}));
	}

	getExpirationTimestamp() {
		if (!this.props.playerModal.expiresAt) return 0;

		return +new Date(this.props.playerModal.expiresAt).setUTCHours(23, 59, 59, 999);
	}

	loadFreespinsRuleOptions() {
		this.props.setPlayerModal((prevState) => ({
			...prevState,
			vipRulesLoading: true,
			vipRulesError: "",
		}));

		this.props.Interface.loadFreespinsRules({ limit: 100, project: this.getPlayerProject() })
			.then((response) => {
				const rules = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
				const options = rules
					.filter((rule) => Number(rule.active) !== 0)
					.map((rule) => ({
						label: rule.reasonName ? `${rule.reasonName} (${rule.reasonCode})` : rule.reasonCode,
						value: rule.reasonCode,
						rule,
					}));

				this.props.setPlayerModal((prevState) => ({
					...prevState,
					vipReasonOptions: options,
					vipRulesLoading: false,
					vipRulesError: "",
				}));
			})
			.catch((error) => {
				const errorText = this.getErrorText(error, "Failed to load freespins rules");

				this.props.setPlayerModal((prevState) => ({
					...prevState,
					vipReasonOptions: [],
					vipRulesLoading: false,
					vipRulesError: errorText,
				}));
			});
	}

	validateFreespinsBase() {
		if (!this.props.playerModal.providerSelected) {
			this.props.Interface.addNoty("Error", "Please select a provider");
			return false;
		}

		if (!this.props.playerModal.gameSelected) {
			this.props.Interface.addNoty("Error", "Please select a game");
			return false;
		}

		const spinsCount = parseInt(this.props.playerModal.spinsCount, 10) || 0;
		if (!this.props.playerModal.spinsCount || spinsCount < 1 || (!this.isVipFreespinsModal() && spinsCount > 50)) {
			this.props.Interface.addNoty("Error", this.isVipFreespinsModal() ? "Please enter spins count" : "Please enter spins count (1-50)");
			return false;
		}

		const betAmount = parseFloat(this.props.playerModal.betAmount) || 0;
		if (betAmount <= 0) {
			this.props.Interface.addNoty("Error", "Please enter bet amount");
			return false;
		}

		if (!this.isVipFreespinsModal() && (!this.props.playerModal.bonusName || this.props.playerModal.bonusName.trim() === "")) {
			this.props.Interface.addNoty("Error", "Please enter bonus name");
			return false;
		}

		if (!this.props.playerModal.expiresAt) {
			this.props.Interface.addNoty("Error", "Please select expiration date");
			return false;
		}

		const expirationDate = this.getExpirationTimestamp();
		const currentDate = +new Date();

		if (expirationDate <= currentDate) {
			this.props.Interface.addNoty("Error", "Expiration date must be in the future");
			return false;
		}

		return true;
	}

	validateVipFreespins(requireSuccessfulCheck = false) {
		if (!this.validateFreespinsBase()) return false;

		if (!this.props.playerModal.vipReasonSelected) {
			this.props.Interface.addNoty("Error", "Please select VIP Support reason");
			return false;
		}

		if (requireSuccessfulCheck && (!this.props.playerModal.vipCheckLogId || this.props.playerModal.vipCheckStatus !== "success")) {
			this.props.Interface.addNoty("Error", "Run successful player check first");
			return false;
		}

		return true;
	}

	isVipCheckButtonDisabled() {
		return (
			this.props.playerModal.vipCheckLoading ||
			this.props.playerModal.vipGrantLoading ||
			!this.props.playerModal.providerSelected ||
			!this.props.playerModal.gameSelected ||
			!this.props.playerModal.spinsCount ||
			!this.props.playerModal.betAmount ||
			!this.props.playerModal.expiresAt ||
			!this.props.playerModal.vipReasonSelected
		);
	}

	isVipGrantButtonDisabled() {
		return (
			this.props.playerModal.vipCheckLoading ||
			this.props.playerModal.vipGrantLoading ||
			this.props.playerModal.vipCheckStatus !== "success" ||
			!this.props.playerModal.vipCheckLogId ||
			this.isVipCheckButtonDisabled()
		);
	}

	buildVipFreespinsPayload(checkLogId = this.props.playerModal.vipCheckLogId) {
		const currency = this.getPlayerCurrency();

		return {
			uid: this.props.playerPage.uid,
			checkLogId,
			reasonCode: this.props.playerModal.vipReasonSelected.value,
			providerId: this.props.playerModal.gameSelected.game.providerID,
			gameId: this.props.playerModal.gameSelected.gameId,
			spinsCount: parseInt(this.props.playerModal.spinsCount, 10),
			betLevel: parseFloat(this.props.playerModal.betAmount),
			currency,
			expiresAt: this.getExpirationTimestamp(),
		};
	}

	checkVipFreespins() {
		if (this.props.playerModal.vipCheckLoading || this.props.playerModal.vipGrantLoading || !this.validateVipFreespins()) return;

		this.props.setPlayerModal((prevState) => ({
			...prevState,
			...this.resetVipFreespinsCheckFields(),
			vipCheckLoading: true,
		}));

		const payload = this.buildVipFreespinsPayload(null);
		delete payload.checkLogId;

		this.props.Interface.checkPlayerFreespins(payload)
			.then((response) => {
				if (response?.eligible) {
					this.props.setPlayerModal((prevState) => ({
						...prevState,
						vipCheckLoading: false,
						vipCheckStatus: "success",
						vipCheckLogId: response.checkLogId,
						vipCheckError: "",
						vipCheckResponse: response,
					}));
					this.props.Interface.addNoty("Success", "Player check passed");
					return;
				}

				const failedReason = response?.failedReason || "Eligibility check failed";
				this.props.setPlayerModal((prevState) => ({
					...prevState,
					vipCheckLoading: false,
					vipCheckStatus: "fail",
					vipCheckLogId: null,
					vipCheckError: failedReason,
					vipCheckResponse: response || null,
				}));
				this.props.Interface.addNoty("Error", failedReason);
			})
			.catch((error) => {
				const errorText = this.getErrorText(error, "Failed to check player");

				this.props.setPlayerModal((prevState) => ({
					...prevState,
					vipCheckLoading: false,
					vipCheckStatus: "fail",
					vipCheckLogId: null,
					vipCheckError: errorText,
					vipCheckResponse: null,
				}));
			});
	}

	createVipFreespins() {
		if (this.props.playerModal.vipGrantLoading || this.props.playerModal.vipCheckLoading || !this.validateVipFreespins(true)) return;

		this.props.setPlayerPage({ ...this.props.playerPage, loaded: false });
		this.props.setPlayerModal((prevState) => ({
			...prevState,
			vipGrantLoading: true,
		}));

		this.props.Interface.vipGrantPlayerFreespins(this.buildVipFreespinsPayload())
			.then((response) => {
				this.props.Interface.addNoty("Success", `VIP freespins created successfully! ID: ${response?.freespinId || response?.grantLogId || "unknown"}`);
				this.toggleValueModal();
				setTimeout(() => {
					this.props.Interface.loadInfo(this.props.playerPage.uid);
					this.props.Interface.loadPlayerFreespins(this.props.playerPage.data.casinoID, 1, this.props.playerPage.uid, "active");
				}, 0);
			})
			.catch((error) => {
				const rawErrorText = this.getErrorText(error, "Failed to create VIP freespins");
				const errorText = rawErrorText === "PLAYER_MAPPING_UNCONFIRMED" ? "Player Casino ID mapping is not confirmed. Grant is unavailable." : rawErrorText;

				this.props.Interface.addNoty("Error", "Failed to create VIP freespins: " + errorText);
				this.props.setPlayerModal((prevState) => ({
					...prevState,
					vipGrantLoading: false,
					vipCheckStatus: "fail",
					vipCheckLogId: null,
					vipCheckError: errorText,
					vipCheckResponse: null,
				}));
				this.props.setPlayerPage({ ...this.props.playerPage, loaded: true });
			});
	}

	managePayment(action, id, uid, note, project) {
		let withdrawals = cloneDeep(this.props.withdrawals.data);
		let withdrawalsToProcess = typeof id === "undefined" ? cloneDeep(this.props.withdrawals.selectedItems) : [{ id, uid, project }];
		const withdrawalsById = new Map(withdrawals.map((item) => [item.id, item]));
		const getWithdrawalAmount = (withdrawalId) => {
			const item = withdrawalsById.get(withdrawalId);
			return item ? item.amount : "";
		};

		withdrawalsToProcess.forEach((withdrawal, i) => {
			withdrawals.forEach((item, j) => {
				if (item.id === withdrawal.id) {
					item.process = true;
				}
			});
		});

		this.props.setWithdrawals({ ...this.props.withdrawals, data: withdrawals });

		let wirhdrawalsStatuses = {},
			checker = setInterval(() => {
				let withdrawalsClone = cloneDeep(this.props.withdrawals.data);

				withdrawalsToProcess.forEach((withdrawal) => {
					withdrawalsClone.forEach((item) => {
						if (item.id === withdrawal.id) {
							item.process = true;
						}
					});
				});

				Object.keys(wirhdrawalsStatuses).forEach((processedWithdrawal) => {
					withdrawalsClone.forEach((item) => {
						if (item.id === parseInt(processedWithdrawal)) {
							item.status = wirhdrawalsStatuses[processedWithdrawal].status;

							if (typeof wirhdrawalsStatuses[processedWithdrawal].balance !== "undefined") {
								item.balance = wirhdrawalsStatuses[processedWithdrawal].balance;
							}

							item.process = false;
						}
					});
				});

				this.props.setWithdrawals({ ...this.props.withdrawals, data: withdrawalsClone, selectedItems: [] });

				if (Object.keys(wirhdrawalsStatuses).length >= withdrawalsToProcess.length) {
					clearInterval(checker);
				}
			}, 500);

		withdrawalsToProcess.forEach((withdrawal, i) => {
			setTimeout(() => {
				const params = {
					id: withdrawal.id,
					uid: withdrawal.uid,
					project: withdrawal.project,
					note: typeof note !== "undefined" ? note : "default",
				};

				this.props.Interface.request("set", { class: "data", module: "withdrawals", method: action }, { ...params })
					.then((data) => {
						wirhdrawalsStatuses[withdrawal.id] = { status: data.status };

						if (typeof data.balance !== "undefined") wirhdrawalsStatuses[withdrawal.id].balance = data.balance;

						if (action === "reject") {
							const writeOffAmount = typeof data.writeOffAmount !== "undefined" ? data.writeOffAmount : getWithdrawalAmount(withdrawal.id);
							this.appendPlayerComment({
								uid: withdrawal.uid,
								reason: params.note,
								amount: writeOffAmount,
								project: withdrawal.project,
							});
						}
					})
					.catch((e) => {
						wirhdrawalsStatuses[withdrawal.id] = { status: e.text };
					});
			}, (i + 1) * 1000);
		});
	}

	isCryptoWithdrawal(withdrawal) {
		return Number(withdrawal?.isCrypto) === 1;
	}

	isOnlyFiatAvailable() {
		if (this.props.playerModal.item) {
			return this.isCryptoWithdrawal(this.props.playerModal.item);
		}

		return this.props.withdrawals.selectedItems.length > 0 && this.props.withdrawals.selectedItems.every((withdrawal) => this.isCryptoWithdrawal(withdrawal));
	}

	changeBalance() {
		this.props.setPlayerPage({ ...this.props.playerPage, loaded: false });

		this.props.Interface.request("set", { class: "users", module: "wallet", method: "balance" }, { uid: this.props.playerPage.uid, amount: this.props.playerModal.value })
			.then((balance) => {
				this.props.setPlayerPage({ ...this.props.playerPage, data: { ...this.props.playerPage.data, balance, balanceCasino: balance * 100 }, loaded: true });

				if (this.props.playerPage.log.type < 5)
					setTimeout(() => {
						this.props.Interface.loadLog(this.props.playerPage.uid);
					}, 250);
			})
			.catch(() => {
				this.props.setPlayerPage({ ...this.props.playerPage, loaded: true });
			});
	}

	patchPlayerBalance(uid, project, balance) {
		this.props.setPlayerPage((prev) => {
			if (!isSamePlayerContext(prev, uid, project)) return prev;

			return {
				...prev,
				data: {
					...prev.data,
					balance,
					balanceCasino: balance * 100,
				},
			};
		});
	}

	schedulePlayerLogReload(uid, project, logModeSignature) {
		if (!logModeSignature || !(Number(logModeSignature.type) < 5)) return;

		setTimeout(() => {
			const currentPlayerPage = getRecoil(playerPageStore);
			if (
				isSamePlayerContext(currentPlayerPage, uid, project) &&
				Number(logModeSignature.type) < 5 &&
				isSameLogModeSignature(currentPlayerPage.log, logModeSignature)
			) {
				this.props.Interface.loadLog(uid, project);
			}
		}, 250);
	}

	changeBalanceReason() {
		const uid = this.props.playerPage.uid;
		const project = this.props.playerPage.data.project;
		const reason = this.props.playerModal.value2.value;
		const amount = this.props.playerModal.value;
		const logModeSignature = getReloadableLogModeSignature(this.props.playerPage.log);

		this.props.setPlayerPage((prev) => (isSamePlayerContext(prev, uid, project) ? { ...prev, loaded: false } : prev));

		this.props.Interface.request("set", { class: "users", module: "wallet", method: "balanceReason" }, { uid, reason, amount, user: localStorage.getItem("access.login") })
			.then((balance) => {
				this.patchPlayerBalance(uid, project, balance);
				this.props.setPlayerPage((prev) => (isSamePlayerContext(prev, uid, project) ? { ...prev, loaded: true } : prev));
				this.appendPlayerComment({
					uid,
					reason,
					amount,
					project,
				});

				this.schedulePlayerLogReload(uid, project, logModeSignature);
			})
			.catch(() => {
				this.props.setPlayerPage((prev) => (isSamePlayerContext(prev, uid, project) ? { ...prev, loaded: true } : prev));
			});
	}

	normalizeWriteOffPresetResult(result) {
		if (
			!result ||
			typeof result !== "object" ||
			Array.isArray(result) ||
			!["success", "partial"].includes(result.status) ||
			typeof result.balance === "undefined" ||
			!Number.isSafeInteger(result.deltaMinor) ||
			result.deltaMinor <= 0 ||
			!Number.isSafeInteger(result.targetMinor) ||
			result.targetMinor <= 0 ||
			result.targetMinor % 100 !== 0 ||
			typeof result.label !== "string" ||
			!result.label.trim() ||
			typeof result.casinoStatus !== "string" ||
			(result.status === "partial" && result.casinoStatus !== "failed") ||
			(result.status === "success" && result.casinoStatus === "failed")
		) {
			throw new Error("Invalid write-off preset response");
		}

		return result;
	}

	changeBalanceWriteOffPreset() {
		const selectedPreset = this.getSelectedWriteOffPreset();
		if (!selectedPreset || this.state.writeOffPresetSubmitting) return;

		const uid = this.props.playerPage.uid;
		const project = this.props.playerPage.data.project;
		const logModeSignature = getReloadableLogModeSignature(this.props.playerPage.log);
		this.writeOffPresetApplyRequestId += 1;
		const requestId = this.writeOffPresetApplyRequestId;
		this.setState({ writeOffPresetSubmitting: true, writeOffPresetError: "" });
		this.props.setPlayerPage((prev) => (isSamePlayerContext(prev, uid, project) ? { ...prev, loaded: false } : prev));

		this.props.Interface.request(
			"set",
			{ class: "users", module: "wallet", method: "balanceWriteOffPreset" },
			{ uid, label: selectedPreset.presetLabel },
		)
			.then((response) => {
				const result = this.normalizeWriteOffPresetResult(response);
				if (requestId !== this.writeOffPresetApplyRequestId || !isSamePlayerContext(this.props.playerPage, uid, project)) return;

				this.props.setPlayerPage((prev) => {
					if (!isSamePlayerContext(prev, uid, project)) return prev;

					return {
						...prev,
						loaded: true,
						data: {
							...prev.data,
							balance: result.balance,
						},
					};
				});
				this.appendPlayerComment({
					uid,
					reason: result.label,
					amount: this.formatBalanceCreditCommentAmount(result.deltaMinor),
					project,
				});
				this.schedulePlayerLogReload(uid, project, logModeSignature);

				if (result.status === "partial") {
					const backendError = typeof result.error === "string" && result.error ? ` ${result.error}` : "";
					this.setState({
						writeOffPresetSubmitting: false,
						writeOffPresetError: `Project balance was updated, but the casino balance was not synchronized. Manual Sync is required.${backendError}`,
					});
					return;
				}

				this.setState({ writeOffPresetSubmitting: false, writeOffPresetError: "" }, () => this.toggleValueModal());
			})
			.catch((error) => {
				if (requestId !== this.writeOffPresetApplyRequestId) return;

				const errorText = this.getErrorText(error, "Failed to apply write-off preset");
				this.props.setPlayerPage((prev) => (isSamePlayerContext(prev, uid, project) ? { ...prev, loaded: true } : prev));
				this.setState({ writeOffPresetSubmitting: false, writeOffPresetError: errorText });
				if (!error?.timestamp) this.props.Interface.addNoty("Error", errorText);
			});
	}

	changeBalanceCredit() {
		if (this.isBalanceCreditSubmitDisabled()) return;

		const uid = this.props.playerPage.uid;
		const project = this.props.playerPage.data.project;
		const creditContext = this.getBalanceCreditContext();
		const logModeSignature = getReloadableLogModeSignature(this.props.playerPage.log);
		const amount = this.state.balanceCreditAmount;
		this.setState({ balanceCreditSubmitting: true });
		this.props.setPlayerPage((prev) => (isSamePlayerContext(prev, uid, project) ? { ...prev, loaded: false } : prev));

		this.props.Interface.request("set", { class: "users", module: "wallet", method: "balanceCredit" }, { uid, amount })
			.then((result) => {
				if (
					!result ||
					typeof result !== "object" ||
					Array.isArray(result) ||
					!Number.isSafeInteger(result.amountMinor) ||
					result.amountMinor <= 0 ||
					typeof result.balance === "undefined"
				) {
					throw new Error("Invalid balance credit response");
				}
				const limitState = normalizeBalanceCreditLimitState({ ...result, enabled: true });

				this.patchPlayerBalance(uid, project, result.balance);
				this.props.setPlayerPage((prev) => (isSamePlayerContext(prev, uid, project) ? { ...prev, loaded: true } : prev));
				const requestToken = createBalanceCreditLimitRequestToken();
				this.props.setPlayerModal((playerModal) => {
					if (!isSameBalanceCreditLimitContext(this.getBalanceCreditContext(), creditContext)) return playerModal;

					return {
						...playerModal,
						balanceCreditLimit: {
							...limitState,
							...creditContext,
							loading: false,
							loaded: true,
							error: "",
							requestToken,
						},
					};
				});
				this.appendPlayerComment({
					uid,
					reason: "Manual credit",
					amount: this.formatBalanceCreditCommentAmount(result.amountMinor),
					project,
					operation: "credit",
				});

				this.schedulePlayerLogReload(uid, project, logModeSignature);
			})
			.catch((error) => {
				const errorText = this.getErrorText(error, "Failed to credit balance");

				this.props.setPlayerPage((prev) => (isSamePlayerContext(prev, uid, project) ? { ...prev, loaded: true } : prev));
				if (isSamePlayerContext(this.props.playerPage, uid, project)) {
					if (/credit.*(?:disabled|limit|exhausted|exceeded|changed|state)/i.test(errorText)) {
						this.loadBalanceCreditLimit();
					}
				}

				if (!error?.timestamp) {
					this.props.Interface.addNoty("Error", errorText);
				}
			});
	}

	formatCommentDate() {
		return new Date().toLocaleString();
	}

	formatCommentAmount(amount) {
		const normalizedAmount = typeof amount === "string" ? amount.replace(/[^0-9.-]/g, "") : amount;
		const amountValue = parseFloat(normalizedAmount);
		return Number.isFinite(amountValue) ? amountValue.toFixed(2) : "0.00";
	}

	buildAutoComment({ reason, amount, operation }) {
		const action = operation === "credit" ? "Balance credit amount" : "Write-off amount";
		const formattedAmount = operation === "credit" ? amount : this.formatCommentAmount(amount);
		return `${action}: ${formattedAmount} RUB | Reason: ${reason} | Date: ${this.formatCommentDate()}`;
	}

	getPlayerComment(uid, project) {
		if (
			isSamePlayerContext(this.props.playerPage, uid, project) &&
			typeof this.props.playerPage.data.comment !== "undefined" &&
			typeof this.props.playerPage.data.publicComment !== "undefined"
		) {
			return Promise.resolve(({private: this.props.playerPage.data.comment || "", public: this.props.playerPage.data.publicComment }));
		}

		return this.props.Interface.request(
			"get",
			{ class: "users", module: "data", method: "info" },
			{ uid, light: true, project: typeof project !== "undefined" ? project : 0 }
		)
			.then((data) => ({ private: data && data.comment ? data.comment : "", public: data && data.publicComment ? data.publicComment : "" }))
			.catch(() => "");
	}

	getUserBalanceForComment(uid, project) {
		if (this.props.playerPage.uid === uid && typeof this.props.playerPage.data.balance !== "undefined") {
			return Promise.resolve(this.props.playerPage.data.balance);
		}

		return this.props.Interface.request("get", { class: "users", module: "data", method: "info" }, { uid, light: true, project: typeof project !== "undefined" ? project : 0 })
			.then((data) => (data && data.balance ? data.balance : ""))
			.catch(() => "");
	}

	appendPlayerComment({ uid, reason, amount, project, operation }) {
		const entry = this.buildAutoComment({ reason, amount, operation });

		this.getPlayerComment(uid, project)
			.then((current) => {
				const nextPrivateComment =
					current  && current.private && current.private.trim() !== "" ? `${entry}\n${current.private}` : entry;

				const nextPublicComment =
					current && current.public  && current.public.trim() !== "" ? `${entry}\n${current.public}` : entry;

				return this.props.Interface.request(
					"set",
					{ class: "users", module: "data", method: "comment" },
					{ uid, comment: nextPrivateComment, project }
				).then(() => {
					this.props.Interface.request(
						"set",
						{ class: "users", module: "data", method: "publicComment" },
						{ uid, comment: nextPublicComment, project }
					).then(() => {
						this.props.setPlayerPage((prev) => {
							if (!isSamePlayerContext(prev, uid, project)) return prev;

							return {
								...prev,
								data: {
									...prev.data,
									comment: nextPrivateComment,
									publicComment: nextPublicComment,
								},
							};
						});
					}).catch((error) => {
						console.log("Couldn't append player comment", error);
					});
				});
			})
			.catch((error) => {
				console.log("Couldn't append player comment", error);
			});
	}

	changeLoyalty() {
		this.props.setPlayerPage({ ...this.props.playerPage, loaded: false });

		this.props.Interface.request("set", { class: "users", module: "wallet", method: "balanceRakeback" }, { uid: this.props.playerPage.uid, amount: this.props.playerModal.value })
			.then((balance) => {
				this.props.setPlayerPage({ ...this.props.playerPage, data: { ...this.props.playerPage.data, balanceRakeback: balance }, loaded: true });
			})
			.catch(() => {
				this.props.setPlayerPage({ ...this.props.playerPage, loaded: true });
			});
	}

	changePartner() {
		this.props.setPlayerPage({ ...this.props.playerPage, loaded: false });

		this.props.Interface.request("set", { class: "users", module: "wallet", method: "balancePartner" }, { uid: this.props.playerPage.uid, amount: this.props.playerModal.value })
			.then((balance) => {
				this.props.setPlayerPage({ ...this.props.playerPage, data: { ...this.props.playerPage.data, balance_r: balance }, loaded: true });
			})
			.catch(() => {
				this.props.setPlayerPage({ ...this.props.playerPage, loaded: true });
			});
	}

	givePromo() {
		const note = this.buildPromoNote();

		if (!this.isPromoValid() || !note) {
			if (!this.props.playerModal.promoReasonSelected) {
				this.props.Interface.addNoty("Error", "Please select a reason");
			} else if (this.props.playerModal.promoReasonSelected.value === "VIP" && (!this.props.playerModal.vipWager || !this.props.playerModal.vipCap)) {
				this.props.Interface.addNoty("Error", "Please fill Wager and Cap");
			} else if (this.props.playerModal.promoReasonSelected.value === "bag_slot" && !this.props.playerModal.promoGameSelected) {
				this.props.Interface.addNoty("Error", "Please select a game");
			}
			return;
		}

		this.props.setPlayerPage({ ...this.props.playerPage, loaded: false });

		this.props.Interface.request("set", { class: "users", module: "promo", method: "give" }, { uid: this.props.playerPage.uid, value: this.props.playerModal.value, note })
			.then(() => {
				this.props.Interface.addNoty("Success", "Promo was successfully created");
				setTimeout(() => {
					this.props.Interface.loadInfo(this.props.playerPage.uid);
				}, 0);
			})
			.catch(() => {
				this.props.setPlayerPage({ ...this.props.playerPage, loaded: true });
			});
	}

	createFreespins() {
		if (!this.validateFreespinsBase()) return;

		this.props.setPlayerPage({ ...this.props.playerPage, loaded: false });

		const spinsCount = parseInt(this.props.playerModal.spinsCount, 10) || 0;
		const betAmount = parseFloat(this.props.playerModal.betAmount) || 0;
		const expirationDate = this.getExpirationTimestamp();

		const freespinData = {
			gameId: this.props.playerModal.gameSelected.gameId,
			providerId: this.props.playerModal.gameSelected.game.providerID,
			spinsCount: spinsCount,
			betLevel: betAmount,
			bonusName: this.props.playerModal.bonusName.trim(),
			currency: this.props.playerPage.data.currency || "RUB",
			expiresAt: expirationDate,
		};

		this.props.Interface.createPlayerFreespins(this.props.playerPage.data.casinoID, freespinData, this.props.playerPage.uid, "active")
			.then(() => {
				this.toggleValueModal();
				setTimeout(() => {
					this.props.Interface.loadInfo(this.props.playerPage.uid);
				}, 0);
			})
			.catch((error) => {
				let errorMessage = "Unknown error";
				if (typeof error === "string") {
					errorMessage = error;
				} else if (error && error.message) {
					errorMessage = error.message;
				} else if (error && error.text) {
					errorMessage = error.text;
				} else if (error && typeof error === "object") {
					errorMessage = JSON.stringify(error);
				}
				this.props.Interface.addNoty("Error", "Failed to create freespins: " + errorMessage);
				this.props.setPlayerPage({ ...this.props.playerPage, loaded: true });
			});
	}

	loadAvailableGames(provider) {
		if (provider === "endorphina") {
			const gameState = this.props.game;

			if (!gameState?.loaded || !gameState?.data) {
				this.props.Interface.addNoty("Error", "Game data is not loaded. Please reload the page.");
				return;
			}

			const endorphinaGames = gameState.data.filter((game) => {
				return game.providerID === 10 && game.active === 1;
			});

			const formattedGames = endorphinaGames.map((game) => ({
				id: game.gameID,
				description: game.name,
				name: game.name,
				gameID: game.gameID,
				providerID: game.providerID,
			}));

			this.props.setPlayerModal((prevState) => ({
				...prevState,
				availableGames: formattedGames,
			}));
		}
	}

	getFilteredGames() {
		const games = this.props.playerModal.availableGames || [];

		return games.map((game) => ({
			label: game.description,
			value: game.description,
			gameId: game.id,
			game: game,
		}));
	}

	sellPromo() {
		this.props.setPlayerPage({ ...this.props.playerPage, loaded: false });

		this.props.Interface.request("set", { class: "users", module: "promo", method: "sell" }, { uid: this.props.playerPage.uid, value: this.props.playerModal.value, count: this.props.playerModal.value2 })
			.then(() => {
				this.props.setPlayerPage({ ...this.props.playerPage, log: { ...this.props.playerPage.log, min: 0, max: 0, limit: 0, type: 5 } });
				setTimeout(() => {
					this.props.Interface.loadInfo(this.props.playerPage.uid);
				}, 500);
			})
			.catch(() => {
				this.props.setPlayerPage({ ...this.props.playerPage, loaded: true });
			});
	}

	addButton() {
		let buttons = localStorage.getItem("withdrawals.buttons") ? [...new Set([...reasonsWithdrawalsForDenial, ...JSON.parse(localStorage.getItem("withdrawals.buttons"))])] : [...reasonsWithdrawalsForDenial];
		buttons.push(this.props.playerModal.value);
		localStorage.setItem("withdrawals.buttons", JSON.stringify(buttons));

		this.props.setConfig({ ...this.props.config, withdrawals: { ...this.props.config.withdrawals, buttons } });
	}

	removeButton(button) {
		let buttons = localStorage.getItem("withdrawals.buttons") ? JSON.parse(localStorage.getItem("withdrawals.buttons")) : [];

		buttons.forEach((item, i) => {
			if (item === button) buttons.splice(i, 1);
		});

		localStorage.setItem("withdrawals.buttons", JSON.stringify(buttons));

		this.props.setConfig({ ...this.props.config, withdrawals: { ...this.props.config.withdrawals, buttons } });

		if (!buttons.length) this.toggleValueModal();
	}

	addPromoReason() {
		const value = this.state.promoReasonInput.trim();

		if (!value.length || this.props.playerModal.promoReasonSubmitting) return;

		this.props.setPlayerModal((prevState) => ({
			...prevState,
			promoReasonSubmitting: true,
			promoReasonsError: "",
		}));

		this.props.Interface.request("set", { class: "users", module: "promo", method: "addGivePromoReason" }, value)
			.then((reasons) => {
				this.props.setPlayerModal((prevState) => ({
					...prevState,
					promoReasonSelected: { label: value, value },
					promoReasonOptions: this.getPromoReasonOptions(Array.isArray(reasons) ? reasons : []),
					promoReasonSubmitting: false,
					promoReasonsError: "",
				}));
				this.setState({ promoReasonInput: "", showPromoReasonInput: false });
				this.props.Interface.addNoty("Success", "Reason added successfully");
			})
			.catch((error) => {
				const errorText = error?.text || error?.message || error?.toString?.() || "Failed to add reason";

				this.props.setPlayerModal((prevState) => ({
					...prevState,
					promoReasonSubmitting: false,
					promoReasonsError: errorText,
				}));
				this.props.Interface.addNoty("Error", errorText);
			});
	}

	handleKeyPress = (event) => {
		if (event.detail.key === "Enter")
			setTimeout(() => {
				this.makeAction();
			}, 0);
	};

	makeAction(action, value) {
		switch (this.props.playerModal.type) {
			case 0:
				if (!this.isBalanceCreditMode() && this.getSelectedWriteOffPreset()) {
					if (this.state.writeOffPresetSubmitting) return;
					this.changeBalanceWriteOffPreset();
					return;
				}
				if (this.isBalanceCreditMode()) {
					if (this.isBalanceCreditSubmitDisabled()) return;
					this.changeBalanceCredit();
					break;
				}
				this.changeBalanceReason();
				break;
			case 1:
				this.changeBalance();
				break;
			case 2:
				this.changePartner();
				break;
			case 3:
				this.changeLoyalty();
				break;
			case 4:
				switch (action) {
					case "only_fiat":
						this.managePayment("only_fiat", !this.props.playerModal.item ? undefined : this.props.playerModal.item.id, !this.props.playerModal.item ? undefined : this.props.playerModal.item.uid, "only_fiat", !this.props.playerModal.item ? undefined : this.props.playerModal.item.project);
						break;
					case 1:
						this.managePayment("cancel", !this.props.playerModal.item ? undefined : this.props.playerModal.item.id, !this.props.playerModal.item ? undefined : this.props.playerModal.item.uid, "verify", !this.props.playerModal.item ? undefined : this.props.playerModal.item.project);
						break;
					case 2:
						this.managePayment("cancel", !this.props.playerModal.item ? undefined : this.props.playerModal.item.id, !this.props.playerModal.item ? undefined : this.props.playerModal.item.uid, "8.3", !this.props.playerModal.item ? undefined : this.props.playerModal.item.project);
						break;
					case 3:
						this.managePayment("cancel", !this.props.playerModal.item ? undefined : this.props.playerModal.item.id, !this.props.playerModal.item ? undefined : this.props.playerModal.item.uid, "8.4", !this.props.playerModal.item ? undefined : this.props.playerModal.item.project);
						break;
					case 4:
						this.managePayment("cancel", !this.props.playerModal.item ? undefined : this.props.playerModal.item.id, !this.props.playerModal.item ? undefined : this.props.playerModal.item.uid, "3.1.2.4", !this.props.playerModal.item ? undefined : this.props.playerModal.item.project);
						break;
					case 5:
						this.managePayment("cancel", !this.props.playerModal.item ? undefined : this.props.playerModal.item.id, !this.props.playerModal.item ? undefined : this.props.playerModal.item.uid, "3.1.2.22", !this.props.playerModal.item ? undefined : this.props.playerModal.item.project);
						break;
					case 6:
						this.managePayment("cancel", !this.props.playerModal.item ? undefined : this.props.playerModal.item.id, !this.props.playerModal.item ? undefined : this.props.playerModal.item.uid, this.props.playerModal.value, !this.props.playerModal.item ? undefined : this.props.playerModal.item.project);
						break;
					case 7:
						this.managePayment("cancel", !this.props.playerModal.item ? undefined : this.props.playerModal.item.id, !this.props.playerModal.item ? undefined : this.props.playerModal.item.uid, value, !this.props.playerModal.item ? undefined : this.props.playerModal.item.project);
						break;
				}
				break;
			case 5:
				switch (action) {
					case 1:
						this.managePayment("reject", !this.props.playerModal.item ? undefined : this.props.playerModal.item.id, !this.props.playerModal.item ? undefined : this.props.playerModal.item.uid, "8.3", !this.props.playerModal.item ? undefined : this.props.playerModal.item.project);
						break;
					case 2:
						this.managePayment("reject", !this.props.playerModal.item ? undefined : this.props.playerModal.item.id, !this.props.playerModal.item ? undefined : this.props.playerModal.item.uid, "8.4", !this.props.playerModal.item ? undefined : this.props.playerModal.item.project);
						break;
					case 3:
						this.managePayment("reject", !this.props.playerModal.item ? undefined : this.props.playerModal.item.id, !this.props.playerModal.item ? undefined : this.props.playerModal.item.uid, "3.1.2.4", !this.props.playerModal.item ? undefined : this.props.playerModal.item.project);
						break;
					case 4:
						this.managePayment("reject", !this.props.playerModal.item ? undefined : this.props.playerModal.item.id, !this.props.playerModal.item ? undefined : this.props.playerModal.item.uid, "3.1.2.22", !this.props.playerModal.item ? undefined : this.props.playerModal.item.project);
						break;
					case 5:
						this.managePayment("reject", !this.props.playerModal.item ? undefined : this.props.playerModal.item.id, !this.props.playerModal.item ? undefined : this.props.playerModal.item.uid, this.props.playerModal.value, !this.props.playerModal.item ? undefined : this.props.playerModal.item.project);
						break;
					case 6:
						this.managePayment("reject", !this.props.playerModal.item ? undefined : this.props.playerModal.item.id, !this.props.playerModal.item ? undefined : this.props.playerModal.item.uid, value, !this.props.playerModal.item ? undefined : this.props.playerModal.item.project);
						break;
				}
				break;
			case 6:
				if (!this.isPromoValid()) return;
				this.givePromo();
				break;
			case 7:
				this.sellPromo();
				break;
			case 8:
				this.addButton();
				break;
			case 9:
				this.removeButton();
				break;
			case 10:
				this.createFreespins();
				return;
			case VIP_FREESPINS_MODAL_TYPE:
				this.createVipFreespins();
				return;
		}

		if (this.props.playerModal.type !== 6 || this.isPromoValid()) this.toggleValueModal();
	}

	render() {
		const isBalanceCreditMode = this.isBalanceCreditMode();
		const hasBalanceDebitAccess = this.hasBalanceDebitAccess();
		const hasBalanceCreditAccess = this.hasBalanceCreditAccess();
		const isBalanceCreditAvailable = this.isBalanceCreditAvailable();
		const balanceCreditAmountError = this.getBalanceCreditAmountError();
		const selectedWriteOffPreset = this.getSelectedWriteOffPreset();

		return (
			<>
				<Modal
					visible={this.props.playerModal.show}
					onDismiss={() => {
						if (!this.state.balanceCreditSubmitting && !this.state.writeOffPresetSubmitting) {
							this.toggleValueModal();
						}
					}}
					header={isBalanceCreditMode ? "Credit balance" : this.props.playerModal.title}
					children={
						<>
							{this.props.playerModal.type === 0 && (
								<Box margin={{ bottom: "m" }}>
									<FormField label="Mode">
										{hasBalanceDebitAccess && hasBalanceCreditAccess ? (
											<SpaceBetween direction="horizontal" size="s">
												<Button disabled={this.state.balanceCreditSubmitting || this.state.writeOffPresetSubmitting} variant={!isBalanceCreditMode ? "primary" : "normal"} onClick={() => this.openBalanceDebit()}>
													Write-off
												</Button>
												<Button disabled={this.state.balanceCreditSubmitting || this.state.writeOffPresetSubmitting} variant={isBalanceCreditMode ? "primary" : "normal"} onClick={() => this.openBalanceCredit()}>
													Credit
												</Button>
											</SpaceBetween>
										) : hasBalanceDebitAccess ? (
											<Box>Write-off</Box>
										) : hasBalanceCreditAccess ? (
											<Box>Credit</Box>
										) : (
											<></>
										)}
									</FormField>
								</Box>
							)}
							{!isBalanceCreditMode && !selectedWriteOffPreset && this.props.playerModal.type !== 9 && this.props.playerModal.type !== 10 && this.props.playerModal.type !== VIP_FREESPINS_MODAL_TYPE ? (
								<Input
									autoComplete={false}
									autoFocus={true}
									inputMode={this.props.playerModal.type < 4 || (this.props.playerModal.type >= 6 && this.props.playerModal.type !== 8 && this.props.playerModal.type !== 9) ? "numeric" : "text"}
									type={this.props.playerModal.type < 4 || (this.props.playerModal.type >= 6 && this.props.playerModal.type !== 8 && this.props.playerModal.type !== 9) ? "number" : "text"}
									onKeyDown={this.handleKeyPress}
									disableBrowserAutocorrect={true}
									placeholder={this.props.playerModal.description ? this.props.playerModal.description + " (" + this.props.playerModal.units + ")" : ""}
									value={this.props.playerModal.value}
									onChange={(input) => {
										this.props.setPlayerModal({ ...this.props.playerModal, value: input.detail.value });
									}}
								/>
							) : this.props.playerModal.type === 9 ? (
								<>
									{" "}
									{this.props.config.withdrawals.buttons.length ? (
										<SpaceBetween direction="horizontal" size="s">
											{[...new Set(this.props.config.withdrawals.buttons)].map((button) => (
												<Box variant="span">
													<Button
														variant="normal"
														onClick={() => {
															this.removeButton(button);
														}}>
														{button}
													</Button>
												</Box>
											))}
										</SpaceBetween>
									) : (
										<></>
									)}
								</>
							) : (
								<></>
							)}

							{this.props.playerModal.type === 0 && !isBalanceCreditMode && (
								<>
									<Select
										filteringType="auto"
										placeholder="Reason"
										className={this.props.styles["reasonSelect"]}
										selectedOption={this.props.playerModal.value2}
										onChange={(option) => {
											const selectedOption = option.detail.selectedOption;
											if (!selectedOption?.isWriteOffPreset) {
												setTimeout(() => {
													this.loadProviderParams();
												}, 0);
											}
											this.selectWriteOffReason(selectedOption);
										}}
										options={this.getWriteOffReasonOptions()}
										disabled={this.state.writeOffPresetSubmitting}
									/>
									{this.state.writeOffPresetsLoading ? (
										<Box margin={{ top: "xs" }} color="text-body-secondary">
											Loading balance target presets...
										</Box>
									) : null}
									{this.state.writeOffPresetsError ? (
										<Box margin={{ top: "xs" }} color="text-status-error">
											Balance target presets are unavailable. Standard reasons remain available.
										</Box>
									) : null}
									{selectedWriteOffPreset ? (
										<Box margin={{ top: "m" }}>
											<FormField label="Target balance after write-off">
												<Input
													autoFocus={true}
													readOnly={true}
													value={this.formatWriteOffPresetTarget(selectedWriteOffPreset.targetMinor)}
													onKeyDown={this.handleKeyPress}
													disabled={this.state.writeOffPresetSubmitting}
												/>
											</FormField>
											{this.state.writeOffPresetError ? (
												<Box margin={{ top: "s" }}>
													<Alert type="error">{this.state.writeOffPresetError}</Alert>
												</Box>
											) : null}
										</Box>
									) : null}
								</>
							)}

							{this.props.playerModal.type === 0 && isBalanceCreditMode && (
								<Box>
									<FormField label="Amount (RUB)" errorText={this.state.balanceCreditAmount && balanceCreditAmountError ? balanceCreditAmountError : ""}>
										<Input
											autoComplete={false}
											autoFocus={true}
											inputMode="decimal"
											type="text"
											onKeyDown={this.handleKeyPress}
											disableBrowserAutocorrect={true}
											placeholder="Enter amount"
											value={this.state.balanceCreditAmount}
											onChange={(input) => {
												this.setState({ balanceCreditAmount: input.detail.value });
											}}
											disabled={!isBalanceCreditAvailable || this.state.balanceCreditSubmitting}
										/>
									</FormField>
									{this.renderBalanceCreditStatus()}
								</Box>
							)}

							{this.props.playerModal.type === 10 || this.props.playerModal.type === VIP_FREESPINS_MODAL_TYPE ? (
								<>
									<Box margin={{ top: "m" }}>
										<FormField label="Provider">
											{(() => {
												const providerOptions = [{ label: "Endorphina", value: "endorphina" }];
												return (
													<Select
														filteringType="auto"
														placeholder="Select Provider"
														selectedOption={this.props.playerModal.providerSelected}
														onChange={(option) => {
															const selectedProvider = providerOptions.find((p) => p.value === option.detail.selectedOption.value);
															this.props.setPlayerModal({
																...this.props.playerModal,
																providerSelected: selectedProvider,
																gameSelected: null,
																availableGames: [],
																betAmount: "",
																bonusName: "",
																expiresAt: "",
																...(this.isVipFreespinsModal() ? this.resetVipFreespinsCheckFields() : {}),
															});
															this.loadAvailableGames(option.detail.selectedOption.value);
														}}
														options={providerOptions}
													/>
												);
											})()}
										</FormField>
									</Box>

									{this.props.playerModal.providerSelected && (
										<Box margin={{ top: "m" }}>
											<FormField label="Game" description="Select game for freespins">
												<Select
													filteringType="auto"
													placeholder="Select Game (type to search...)"
													selectedOption={this.props.playerModal.gameSelected}
													onChange={(option) => {
														this.updateFreespinsFields({ gameSelected: option.detail.selectedOption }, this.isVipFreespinsModal());
													}}
													options={this.getFilteredGames()}
												/>
											</FormField>
										</Box>
									)}

									{this.props.playerModal.providerSelected && (
										<Box margin={{ top: "m" }}>
											<FormField label="Spins Count" description={this.isVipFreespinsModal() ? "Number of free spins" : "Number of free spins (1-50)"}>
												<Input
													type="number"
													inputMode="numeric"
													placeholder="Enter number of spins"
													value={this.props.playerModal.spinsCount || ""}
													onChange={(input) => {
														this.updateFreespinsFields({ spinsCount: input.detail.value }, this.isVipFreespinsModal());
													}}
												/>
											</FormField>
										</Box>
									)}

									{this.props.playerModal.providerSelected && (
										<Box margin={{ top: "m" }}>
											<FormField label="Bet Amount" description="Bet amount per spin (e.g., 100 = 100.00 RUB, decimals added automatically)">
												<Input
													type="number"
													inputMode="decimal"
													placeholder="Enter bet amount (e.g., 100 for 100.00 RUB)"
													value={this.props.playerModal.betAmount || ""}
													onChange={(input) => {
														this.updateFreespinsFields({ betAmount: input.detail.value }, this.isVipFreespinsModal());
													}}
												/>
											</FormField>
										</Box>
									)}

									{this.props.playerModal.providerSelected && this.isVipFreespinsModal() && (
										<>
											<Box margin={{ top: "m" }}>
												<FormField label="Reason" description="Required VIP Support grant reason">
													<Select
														filteringType="auto"
														placeholder={this.props.playerModal.vipRulesLoading ? "Loading reasons..." : this.props.playerModal.vipReasonOptions.length ? "Select reason" : "No active reasons available"}
														selectedOption={this.props.playerModal.vipReasonSelected}
														onChange={(option) => {
															this.updateFreespinsFields({ vipReasonSelected: option.detail.selectedOption }, true);
														}}
														statusType={this.props.playerModal.vipRulesLoading ? "loading" : "finished"}
														disabled={this.props.playerModal.vipRulesLoading || !this.props.playerModal.vipReasonOptions.length}
														options={this.props.playerModal.vipReasonOptions}
													/>
												</FormField>
											</Box>
											{this.props.playerModal.vipRulesError ? (
												<Box margin={{ top: "xs" }} color="text-status-error">
													{this.props.playerModal.vipRulesError}
												</Box>
											) : (
												<></>
											)}
										</>
									)}

									{this.props.playerModal.providerSelected && !this.isVipFreespinsModal() && (
										<Box margin={{ top: "m" }}>
											<FormField label="Bonus Name" description="Name for the freespins bonus">
												<Input
													type="text"
													placeholder="Enter bonus name"
													value={this.props.playerModal.bonusName || ""}
													onChange={(input) => {
														this.props.setPlayerModal({
															...this.props.playerModal,
															bonusName: input.detail.value,
														});
													}}
												/>
											</FormField>
										</Box>
									)}

									{this.props.playerModal.providerSelected && (
										<Box margin={{ top: "m" }}>
											<FormField label="Expiration Date" description="When freespins will expire">
												<Input
													type="date"
													placeholder="Select expiration date"
													value={this.props.playerModal.expiresAt || ""}
													onChange={(input) => {
														this.updateFreespinsFields({ expiresAt: input.detail.value }, this.isVipFreespinsModal());
													}}
												/>
											</FormField>
										</Box>
									)}

									{this.props.playerModal.providerSelected && this.isVipFreespinsModal() && (
										<Box margin={{ top: "m" }}>
											<SpaceBetween direction="vertical" size="xs">
												{this.props.playerModal.vipCheckStatus === "success" ? (
													<Box color="text-status-success">
														Player check passed. checkLogId: {this.props.playerModal.vipCheckLogId}
													</Box>
												) : (
													<></>
												)}
												{this.props.playerModal.vipCheckStatus === "fail" && this.props.playerModal.vipCheckError ? (
													<Box color="text-status-error">
														{this.props.playerModal.vipCheckError}
													</Box>
												) : (
													<></>
												)}
											</SpaceBetween>
										</Box>
									)}
								</>
							) : this.props.playerModal.type === 6 ? (
								<>
									<Box margin={{ top: "m" }}>
										<FormField label="Reason">
											<Select
												filteringType="auto"
												placeholder={this.props.playerModal.promoReasonsLoading ? "Loading reasons..." : this.props.playerModal.promoReasonOptions.length ? "Select reason" : "No reasons available"}
												selectedOption={this.props.playerModal.promoReasonSelected}
												onChange={(option) => {
													const selectedOption = option.detail.selectedOption;
													const isVipReason = selectedOption?.value === "VIP";

													this.props.setPlayerModal({
														...this.props.playerModal,
														promoReasonSelected: selectedOption,
														promoGameSelected: selectedOption?.value === "bag_slot" ? this.props.playerModal.promoGameSelected : null,
														vipWager: isVipReason ? this.props.playerModal.vipWager : "",
														vipCap: isVipReason ? this.props.playerModal.vipCap : "",
													});
												}}
												statusType={this.props.playerModal.promoReasonsLoading ? "loading" : "finished"}
												disabled={this.props.playerModal.promoReasonsLoading || !this.props.playerModal.promoReasonOptions.length}
												options={this.props.playerModal.promoReasonOptions}
											/>
										</FormField>
									</Box>
									{this.props.playerModal.promoReasonsError ? (
										<Box margin={{ top: "xs" }} color="text-status-error">
											{this.props.playerModal.promoReasonsError}
										</Box>
									) : (
										<></>
									)}
									<Box margin={{ top: "xs" }}>
										<SpaceBetween direction="horizontal" size="s">
											{this.props.user.level >= 6 || this.props.user.showColumn.includes("addPromoReason") ? (
												<Button
													variant="normal"
													onClick={(event) => {
														event.preventDefault();
														this.setState({ showPromoReasonInput: !this.state.showPromoReasonInput, promoReasonInput: "" });
													}}>
													{this.state.showPromoReasonInput ? "Hide " : "Show "}
													add reason
												</Button>
											) : null}
											{this.props.playerModal.promoReasonsError ? (
												<Button
													variant="normal"
													onClick={() => {
														this.loadPromoReasonOptions();
													}}>
													Retry
												</Button>
											) : (
												<></>
											)}
										</SpaceBetween>
										{this.state.showPromoReasonInput && (
											<Box margin={{ top: "xs" }}>
												<SpaceBetween direction="horizontal" size="s">
													<Box variant="div">
														<Input
															placeholder="Enter reason"
															value={this.state.promoReasonInput}
															onChange={(input) => {
																this.setState({ promoReasonInput: input.detail.value });
															}}
														/>
													</Box>
													<Button
														variant="primary"
														disabled={!this.state.promoReasonInput.trim().length || this.props.playerModal.promoReasonSubmitting}
														onClick={() => {
															this.addPromoReason();
														}}>
														{this.props.playerModal.promoReasonSubmitting ? "Saving..." : "Save"}
													</Button>
												</SpaceBetween>
											</Box>
										)}
									</Box>
									{this.props.playerModal.promoReasonSelected?.value === "bag_slot" && (
										<Box margin={{ top: "m" }}>
											<FormField label="Game" description="Select active game">
												<Select
													filteringType="manual"
													placeholder="Select game"
													onLoadItems={({ detail }) => this.handlePromoGameSearch(detail.filteringText)}
													selectedOption={this.props.playerModal.promoGameSelected}
													onChange={(option) => {
														this.props.setPlayerModal({
															...this.props.playerModal,
															promoGameSelected: option.detail.selectedOption,
														});
													}}
													statusType={!this.props.game.loaded ? "loading" : "finished"}
													disabled={this.state.promoGames.length === 0}
													options={this.state.filteredPromoGames}
												/>
											</FormField>
										</Box>
									)}
									{this.props.playerModal.promoReasonSelected?.value === "VIP" && (
										<>
											<Box margin={{ top: "m" }}>
												<FormField label="Wager" description="Required wager amount">
													<Input
														type="number"
														inputMode="numeric"
														placeholder="Enter wager"
														value={this.props.playerModal.vipWager || ""}
														onChange={(input) => {
															this.props.setPlayerModal({
																...this.props.playerModal,
																vipWager: input.detail.value.replace(/\D/g, ""),
															});
														}}
													/>
												</FormField>
											</Box>
											<Box margin={{ top: "m" }}>
												<FormField label="Cap" description="Maximum withdrawal amount in RUB">
													<Input
														type="number"
														inputMode="numeric"
														placeholder="Enter cap"
														value={this.props.playerModal.vipCap || ""}
														onChange={(input) => {
															this.props.setPlayerModal({
																...this.props.playerModal,
																vipCap: input.detail.value.replace(/\D/g, ""),
															});
														}}
													/>
												</FormField>
											</Box>
										</>
									)}
								</>
							) : this.props.playerModal.type >= 6 && this.props.playerModal.type !== 8 && this.props.playerModal.type !== 9 ? (
								<Box
									margin={{ top: "m" }}
									children={
										<Input
											onKeyDown={this.handleKeyPress}
											disableBrowserAutocorrect={true}
											placeholder={this.props.playerModal.description2 ? this.props.playerModal.description2 + " (" + this.props.playerModal.units2 + ")" : ""}
											value={this.props.playerModal.value2}
											onChange={(input) => {
												this.props.setPlayerModal({ ...this.props.playerModal, value2: input.detail.value });
											}}
										/>
									}
								/>
							) : (
								<></>
							)}
						</>
					}
					footer={
						<>
							{this.props.playerModal.type < 4 || (this.props.playerModal.type >= 6 && this.props.playerModal.type !== 9) ? (
								<>
									<Box
										variant="span"
										margin={{ right: "s" }}
										children={
											<Button
												variant="normal"
												disabled={this.state.balanceCreditSubmitting || this.state.writeOffPresetSubmitting}
												onClick={() => {
													this.toggleValueModal();
												}}
												children="Close"
											/>
										}
									/>
									{this.props.playerModal.type === VIP_FREESPINS_MODAL_TYPE ? (
										<Box
											variant="span"
											margin={{ right: "s" }}
											children={
												<Button
													variant="normal"
													disabled={this.isVipCheckButtonDisabled()}
													onClick={() => {
														this.checkVipFreespins();
													}}>
													{this.props.playerModal.vipCheckLoading ? "Checking..." : "Check player"}
												</Button>
											}
										/>
									) : (
										<></>
									)}
									<Box
										variant="span"
										children={
											<Button
												disabled={(this.props.playerModal.type === 0 && (isBalanceCreditMode ? this.isBalanceCreditSubmitDisabled() : selectedWriteOffPreset ? this.state.writeOffPresetSubmitting : this.props.playerModal.value2 === "" || this.props.playerModal.value === "")) || (this.props.playerModal.type === 6 && (!this.isPromoValid() || this.props.playerModal.promoReasonSubmitting)) || (this.props.playerModal.type === VIP_FREESPINS_MODAL_TYPE && this.isVipGrantButtonDisabled())}
												variant="primary"
												onClick={() => {
													this.makeAction();
												}}
												children={this.props.playerModal.type === VIP_FREESPINS_MODAL_TYPE ? (this.props.playerModal.vipGrantLoading ? "Granting..." : "Grant freespins") : isBalanceCreditMode ? (this.state.balanceCreditSubmitting ? "Crediting..." : "Credit") : selectedWriteOffPreset && this.state.writeOffPresetSubmitting ? "Applying..." : "Ok"}
											/>
										}
									/>
								</>
							) : this.props.playerModal.type === 4 ? (
								<SpaceBetween className={this.props.styles["withdrawalsButtons"]} direction="vertical" size="m">
									<SpaceBetween direction="horizontal" size="s">
										<Box variant="span">
											<Button
												variant="normal"
												onClick={() => {
													this.makeAction(1);
												}}>
												Verify
											</Button>
										</Box>
										<Box variant="span">
											<Button
												variant="normal"
												onClick={() => {
													this.makeAction(2);
												}}>
												8.3
											</Button>
										</Box>
										<Box variant="span">
											<Button
												variant="normal"
												onClick={() => {
													this.makeAction(3);
												}}>
												8.4
											</Button>
										</Box>
										<Box variant="span">
											<Button
												variant="normal"
												onClick={() => {
													this.makeAction(4);
												}}>
												3.1.2.4
											</Button>
										</Box>
										<Box variant="span">
											<Button
												variant="normal"
												onClick={() => {
													this.makeAction(5);
												}}>
												3.1.2.22
											</Button>
										</Box>
									</SpaceBetween>
									{this.isOnlyFiatAvailable() || this.props.config.withdrawals.buttons.length ? (
										<SpaceBetween direction="horizontal" size="s">
											{this.isOnlyFiatAvailable() ? (
												<Box variant="span">
													<Button
														variant="normal"
														onClick={() => {
															this.makeAction("only_fiat");
														}}>
														Only_fiat
													</Button>
												</Box>
											) : null}
											{[...new Set(this.props.config.withdrawals.buttons)].map((button) => (
												<Box variant="span">
													<Button
														variant="normal"
														onClick={() => {
															this.makeAction(7, button);
														}}>
														{button}
													</Button>
												</Box>
											))}
										</SpaceBetween>
									) : (
										<></>
									)}
									<SpaceBetween direction="horizontal" size="s">
										<Box
											variant="span"
											children={
												<Button
													variant="normal"
													onClick={() => {
														this.toggleValueModal();
													}}>
													Close
												</Button>
											}
										/>
										<Box
											variant="span"
											children={
												<Button
													variant="primary"
													onClick={() => {
														this.makeAction(6);
													}}>
													Ok
												</Button>
											}
										/>
									</SpaceBetween>
								</SpaceBetween>
							) : this.props.playerModal.type === 9 ? (
								<Box
									variant="span"
									children={
										<Button
											variant="primary"
											onClick={() => {
												this.removeButton(5);
											}}>
											Ok
										</Button>
									}
								/>
							) : (
								<SpaceBetween className={this.props.styles["withdrawalsButtons"]} direction="vertical" size="m">
									<SpaceBetween direction="horizontal" size="s">
										<Box variant="span">
											<Button
												variant="normal"
												onClick={() => {
													this.makeAction(1);
												}}>
												8.3
											</Button>
										</Box>
										<Box variant="span">
											<Button
												variant="normal"
												onClick={() => {
													this.makeAction(2);
												}}>
												8.4
											</Button>
										</Box>
										<Box variant="span">
											<Button
												variant="normal"
												onClick={() => {
													this.makeAction(3);
												}}>
												3.1.2.4
											</Button>
										</Box>
										<Box variant="span">
											<Button
												variant="normal"
												onClick={() => {
													this.makeAction(4);
												}}>
												3.1.2.22
											</Button>
										</Box>
									</SpaceBetween>
									{this.props.config.withdrawals.buttons.length ? (
										<SpaceBetween direction="horizontal" size="s">
											{[...new Set(this.props.config.withdrawals.buttons)].map((button) => (
												<Box variant="span">
													<Button
														variant="normal"
														onClick={() => {
															this.makeAction(6, button);
														}}>
														{button}
													</Button>
												</Box>
											))}
										</SpaceBetween>
									) : (
										<></>
									)}
									<SpaceBetween direction="horizontal" size="s">
										<Box
											variant="span"
											children={
												<Button
													variant="normal"
													onClick={() => {
														this.toggleValueModal();
													}}>
													Close
												</Button>
											}
										/>
										<Box
											variant="span"
											children={
												<Button
													variant="primary"
													onClick={() => {
														this.makeAction(5);
													}}>
													Ok
												</Button>
											}
										/>
									</SpaceBetween>
								</SpaceBetween>
							)}
						</>
					}
				/>
			</>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const user = useRecoilValue(userStore);
		const players = useRecoilValue(playerStore);
		const setPlayers = useSetRecoilState(playerStore);
		const config = useRecoilValue(configStore);
		const setConfig = useSetRecoilState(configStore);
		const playerPage = useRecoilValue(playerPageStore);
		const setPlayerPage = useSetRecoilState(playerPageStore);
		const game = useRecoilValue(gameStore);
		const setGame = useSetRecoilState(gameStore);
		const playerModal = useRecoilValue(playerModalStore);
		const setPlayerModal = useSetRecoilState(playerModalStore);
		const withdrawals = useRecoilValue(withdrawalStore);
		const setWithdrawals = useSetRecoilState(withdrawalStore);

		return <Component {...props} user={user} players={players} setPlayers={setPlayers} config={config} setConfig={setConfig} playerModal={playerModal} setPlayerModal={setPlayerModal} playerPage={playerPage} setPlayerPage={setPlayerPage} game={game} setGame={setGame} withdrawals={withdrawals} setWithdrawals={setWithdrawals} />;
	};
}

export default withRecoil(PlayerModal);
